import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { EASE } from "@/lib/motion";
import { useAuth } from "@/lib/AuthContext";

const LINKS = [
  { to: "/tax-services", label: "Tax Services" },
  { to: "/business-services", label: "Business Services" },
  { to: "/how-it-works", label: "How It Works" },
  { to: "/pricing", label: "Pricing" },
  { to: "/book", label: "Book a Call" },
  { to: "/resources", label: "Resources" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const { user } = useAuth();

  useEffect(() => {
    let raf = null;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        raf = null;
        setScrolled((prev) => {
          const next = window.scrollY > 24;
          return prev === next ? prev : next;
        });
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 z-50 w-full border-b transition-colors duration-500 ${
        scrolled || open ? "border-steel-border/70 bg-steel-bg/90 backdrop-blur-md" : "border-transparent bg-transparent"
      }`}
    >
      <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-6 lg:px-10">
        <Link to="/" data-testid="nav-logo" className="group flex flex-col leading-none">
          <span className="font-display text-lg font-bold tracking-[0.18em] text-steel-text">
            STEELE <span className="text-steel-brass">INDUSTRIES</span>
          </span>
          <span className="mt-1 font-mono text-[9px] uppercase tracking-[0.3em] text-steel-muted">
            Tax Preparation & Accounting
          </span>
          {user && (
            <span
              data-testid="nav-signed-in-as"
              className="mt-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-steel-brass"
            >
              Signed in as: {user.name || user.email}
            </span>
          )}
        </Link>

        <nav className="hidden items-center gap-7 xl:flex" data-testid="nav-desktop">
          {LINKS.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-link-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `text-[13px] font-medium tracking-wide transition-colors duration-300 ${
                  isActive ? "text-steel-text" : "text-steel-secondary hover:text-steel-text"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-4 xl:flex">
          <Link
            to="/portal"
            data-testid="client-portal-link"
            className="text-[13px] font-medium text-steel-secondary transition-colors duration-300 hover:text-steel-text"
          >
            Client Portal
          </Link>
          <Link
            to="/get-started"
            data-testid="start-return-btn"
            className="group flex items-center gap-2 bg-steel-accent px-5 py-2.5 text-[13px] font-semibold tracking-wide text-white shadow-[0_0_20px_rgba(168,85,247,0.35)] transition-[background-color,box-shadow] duration-300 hover:bg-steel-accentHover hover:shadow-[0_0_32px_rgba(168,85,247,0.55)]"
          >
            START YOUR RETURN
            <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
          </Link>
        </div>

        <button
          data-testid="mobile-menu-btn"
          onClick={() => setOpen(!open)}
          className="text-steel-text xl:hidden"
          aria-label="Toggle menu"
        >
          {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
        </button>
      </div>

      <AnimatePresence>
        {open && (
          <motion.nav
            data-testid="nav-mobile"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            className="overflow-hidden border-t border-steel-border/50 bg-steel-bg/95 backdrop-blur-md xl:hidden"
          >
            <div className="flex flex-col gap-1 px-6 py-6">
              {LINKS.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  className="border-b border-steel-border/40 py-3 text-sm font-medium text-steel-secondary transition-colors hover:text-steel-text"
                >
                  {l.label}
                </NavLink>
              ))}
              <div className="mt-4 flex flex-col gap-3">
                {user && (
                  <p data-testid="nav-signed-in-as-mobile" className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel-brass">
                    Signed in as: {user.name || user.email}
                  </p>
                )}
                <Link to="/portal" className="py-2 text-sm font-medium text-steel-secondary">
                  Client Portal
                </Link>
                <Link
                  to="/get-started"
                  className="flex items-center justify-center gap-2 bg-steel-accent px-5 py-3 text-sm font-semibold text-white"
                >
                  START YOUR RETURN <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}
